import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbar } from '@mui/x-data-grid';
import { FrameworkTemplate } from '../../models/types';

interface TemplateListProps {
  templates: FrameworkTemplate[];
  onSelect: (template: FrameworkTemplate) => void;
  onCreate: (name: string, description: string) => void;
  onClone: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
}

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  onSelect,
  onCreate,
  onClone,
  onDelete
}) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openCloneDialog, setOpenCloneDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogState, setDialogState] = useState({
    templateId: '',
    name: '',
    description: ''
  });

  // Enhanced templates with section count for the grid
  const enhancedTemplates = useMemo(() => 
    templates.map(template => ({
      ...template,
      sectionCount: template.sections.length,
    })), 
    [templates]
  );

  // Column definitions for DataGrid
  const columns: GridColDef[] = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Template Name', 
      flex: 1, 
      minWidth: 180,
      renderCell: (params) => (
        <Tooltip title="Click to edit">
          <Typography 
            sx={{ 
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => onSelect(params.row)}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ) 
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 2, 
      minWidth: 250 
    },
    { 
      field: 'sectionCount', 
      headerName: 'Sections', 
      type: 'number', 
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit Template">
            <IconButton size="small" onClick={() => onSelect(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clone Template">
            <IconButton size="small" onClick={() => handleCloneClick(params.row)}>
              <CloneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Template">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], []);

  const handleCreateClick = () => {
    setDialogState({
      templateId: '',
      name: '',
      description: ''
    });
    setOpenCreateDialog(true);
  };

  const handleCreateSubmit = () => {
    onCreate(dialogState.name, dialogState.description);
    setOpenCreateDialog(false);
  };

  const handleCloneClick = (template: FrameworkTemplate) => {
    setDialogState({
      templateId: template.id,
      name: `${template.name} (Copy)`,
      description: template.description
    });
    setOpenCloneDialog(true);
  };

  const handleCloneSubmit = () => {
    onClone(dialogState.templateId, dialogState.name);
    setOpenCloneDialog(false);
  };

  const handleDeleteClick = (template: FrameworkTemplate) => {
    setDialogState({
      templateId: template.id,
      name: template.name,
      description: template.description
    });
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(dialogState.templateId);
    setOpenDeleteDialog(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Framework Templates</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
        >
          Create New Template
        </Button>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          height: 'calc(100vh - 240px)', 
          width: '100%',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          }
        }}
      >
        <DataGrid
          rows={enhancedTemplates}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 300 },
            },
          }}
          sx={{
            boxShadow: 0,
            border: 0,
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
      </Paper>

      {/* Create Template Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            type="text"
            fullWidth
            value={dialogState.name}
            onChange={(e) => setDialogState({ ...dialogState, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={dialogState.description}
            onChange={(e) => setDialogState({ ...dialogState, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCreateSubmit} color="primary" disabled={!dialogState.name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Template Dialog */}
      <Dialog open={openCloneDialog} onClose={() => setOpenCloneDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Template Name"
            type="text"
            fullWidth
            value={dialogState.name}
            onChange={(e) => setDialogState({ ...dialogState, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCloneDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCloneSubmit} color="primary" disabled={!dialogState.name}>
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{dialogState.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateList;
